/**
 * network/PeopleYouMayKnow — network-style suggestions widget.
 *
 * Uses GET /api/network/suggestions and offers a "Connect" (send request)
 * button per row. Once the request is sent, the button flips to "Pending".
 * Already-connected members are hidden (server excludes them).
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { UserPlus, Check, Users, Loader2, Clock } from 'lucide-react';

const PeopleYouMayKnow = () => {
  const { API } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-row status map: undefined = default, 'pending' = request sent, 'accepted' = auto-mutual
  const [status, setStatus] = useState({});

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/network/suggestions`)
      .then((r) => { if (alive) setItems(r.data?.suggestions || []); })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [API]);

  const connect = async (id) => {
    setStatus((s) => ({ ...s, [id]: 'busy' }));
    try {
      const { data } = await axios.post(`${API}/network/connect/${id}`, {});
      setStatus((s) => ({ ...s, [id]: data.status === 'accepted' ? 'accepted' : 'pending' }));
      toast.success(data.status === 'accepted' ? 'Connected!' : 'Request sent');
    } catch (e) {
      setStatus((s) => ({ ...s, [id]: undefined }));
      toast.error(e.response?.data?.detail || 'Could not send request');
    }
  };

  if (loading) return (
    <div className="pro-card p-4 text-xs text-muted-foreground inline-flex items-center gap-2">
      <Loader2 className="w-3 h-3 animate-spin" /> loading suggestions…
    </div>
  );
  if (items.length === 0) return null;

  return (
    <aside className="pro-card p-4" data-testid="people-you-may-know">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-primary" />
        <span className="font-heading font-extrabold text-sm">People you may know</span>
      </div>
      <ul className="space-y-2">
        {items.map((p) => {
          const s = status[p.id];
          return (
            <li key={p.id} className="flex items-center gap-2" data-testid={`suggestion-${p.id}`}>
              <Link to={`/u/${p.handle || p.id}`} className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                {p.avatar_url
                  ? <img src={p.avatar_url} alt="" className="h-full w-full object-cover" />
                  : (p.name || '?').slice(0, 2).toUpperCase()}
              </Link>
              <div className="min-w-0 flex-1">
                <Link to={`/u/${p.handle || p.id}`} className="text-xs font-semibold truncate block hover:text-primary transition-colors">
                  {p.name}
                </Link>
                <div className="text-[10px] text-muted-foreground truncate">{p.reason}</div>
              </div>
              {s === 'accepted' ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-primary flex-shrink-0">
                  <Check className="w-3 h-3" /> Connected
                </span>
              ) : s === 'pending' ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground flex-shrink-0">
                  <Clock className="w-3 h-3" /> Pending
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => connect(p.id)}
                  disabled={s === 'busy'}
                  data-testid={`suggestion-connect-${p.id}`}
                  className="rounded-full h-7 text-[11px] font-bold px-2 flex-shrink-0"
                >
                  {s === 'busy' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><UserPlus className="w-3 h-3 mr-1" /> Connect</>}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default PeopleYouMayKnow;
